import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Save, Share2, PartyPopper, SlidersHorizontal, AlertTriangle, Sparkles, RotateCcw, Lock } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money } from "@/lib/constants";
import { StopCard, TravelLeg } from "@/components/planner/StopCard";
import { PickPlaceDialog } from "@/components/planner/PickPlaceDialog";
import { ReasonDialog } from "@/components/planner/ReasonDialog";
import { ShareDialog } from "@/components/planner/ShareDialog";
import { CheckinDialog } from "@/components/planner/CheckinDialog";
import { SponsoredAds } from "@/components/planner/SponsoredAds";

const DISLIKE_REASONS = ["Not my vibe", "Too expensive", "Too far", "Too crowded", "Dietary mismatch", "Been there already"];
const REPORT_ISSUES = ["Closed permanently", "Wrong hours", "Not as described", "Doesn't exist", "Other"];

export const Itinerary = ({ plan, setPlan, onEdit, activeSid, setActiveSid }) => {
  const { user } = useAuth();
  const nav = useNavigate();
  const owner = plan.role === "owner";
  const [pick, setPick] = useState(null);
  const [dislike, setDislike] = useState(null);
  const [report, setReport] = useState(null);
  const [share, setShare] = useState(false);
  const [checkin, setCheckin] = useState(false);
  const [drag, setDrag] = useState(null);

  const update = async (entries) => {
    try {
      const { data } = await api.put(`/plans/${plan.id}`, { stops: entries });
      setPlan(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const ids = plan.stops.map((s) => ({ sid: s.sid }));
  const reorder = (from, to) => {
    if (to < 0 || to >= ids.length || from === to) return;
    const next = [...ids];
    next.splice(to, 0, next.splice(from, 1)[0]);
    update(next);
  };
  const actions = {
    select: setActiveSid,
    move: (i, d) => reorder(i, i + d),
    skip: (sid) => (ids.length > 1 ? update(ids.filter((x) => x.sid !== sid)) : toast.error("Keep at least one stop.")),
    replace: (stop) => setPick({ replacing: stop }),
    like: async (stop) => { await api.post("/feedback", { plan_id: plan.id, sid: stop.sid, vote: "up" }); setPlan({ ...plan, stops: plan.stops.map((s) => (s.sid === stop.sid ? { ...s, feedback: "up" } : s)) }); toast.success("Noted. More like this."); },
    dislike: (stop) => setDislike(stop),
    report: (stop) => setReport(stop),
    join: async (sid) => { try { const { data } = await api.post(`/plans/${plan.id}/stops/${sid}/join`); setPlan(data); } catch (e) { toast.error(errMsg(e)); } },
  };
  const onPick = (c) => {
    const r = pick.replacing;
    update(r ? ids.map((x) => (x.sid === r.sid ? { pid: c.pid, why: `Your pick, swapped in for ${r.name}.` } : x)) : [...ids, { pid: c.pid, why: "Added by you." }]);
    setPick(null);
  };
  const save = async () => { const { data } = await api.post(`/plans/${plan.id}/save`); setPlan(data); toast.success("Saved privately to My Plans"); };
  const t = plan.totals || {};

  return (
    <div className="p-5 lg:p-6 space-y-4" data-testid="itinerary-panel">
      <div className="rise">
        <div className="flex items-center gap-2 flex-wrap">
          {plan.ai ? <span className="text-[11px] font-mono font-semibold px-2 py-0.5 rounded-full bg-[#EAF4EC] text-[#23603F] flex items-center gap-1"><Sparkles className="w-3 h-3" /> Personalized by AI</span>
            : <span className="text-[11px] font-mono font-semibold px-2 py-0.5 rounded-full bg-stone-100 text-stone-600">Quick match (AI unavailable)</span>}
          <span data-testid="plan-status-badge" className="text-[11px] font-mono font-semibold px-2 py-0.5 rounded-full bg-stone-100 text-stone-600 flex items-center gap-1"><Lock className="w-3 h-3" /> {owner ? (plan.status === "draft" ? "Draft · private" : `${plan.status} · ${plan.shared_with?.length ? `shared with ${plan.shared_with.length}` : "private"}`) : `Shared by ${plan.owner?.name}`}</span>
        </div>
        <h1 className="mt-2 text-3xl font-extrabold tracking-tight leading-tight" data-testid="plan-title">{plan.title}</h1>
        {plan.summary && <p className="mt-1.5 text-sm text-stone-600">{plan.summary}</p>}
        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
          {[["Stops", t.stops], ["Est. cost / person", money(t.cost_min, t.cost_max)], ["Wraps up", `~${t.end}`]].map(([k, v]) => (
            <div key={k} className="rounded-xl bg-white border border-stone-200 py-2"><p className="font-mono font-bold text-stone-900" data-testid={`plan-total-${k.split(" ")[0].toLowerCase()}`}>{v}</p><p className="text-[10px] uppercase tracking-wider text-stone-500">{k}</p></div>
          ))}
        </div>
        {!owner && !plan.start && <p className="mt-2 text-xs text-stone-500">{plan.owner?.name}'s starting point is private.</p>}
      </div>
      {plan.warnings?.map((w) => (
        <p key={w} data-testid="plan-warning" className="text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-xl px-3 py-2 flex gap-2"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> {w}</p>
      ))}
      <div>
        {plan.stops.map((s, i) => (
          <div key={s.sid}>
            {(i > 0 || owner) && <TravelLeg stop={s} n={i + 1} mode={plan.inputs?.transport || "walk"} />}
            <StopCard stop={s} index={i} active={activeSid === s.sid} owner={owner} isMe={user?.id} actions={actions}
              dragProps={owner ? { draggable: true, onDragStart: () => setDrag(i), onDragOver: (e) => e.preventDefault(), onDrop: () => { reorder(drag, i); setDrag(null); } } : {}} />
          </div>
        ))}
      </div>
      {owner && (
        <>
          <button data-testid="btn-add-stop" onClick={() => setPick({ replacing: null })} className="w-full border-2 border-dashed border-stone-300 hover:border-[#2F7D55] hover:text-[#23603F] text-stone-600 font-semibold rounded-2xl py-3 flex items-center justify-center gap-2 transition-colors">
            <Plus className="w-4 h-4" /> Add a stop
          </button>
          <div className="grid grid-cols-2 gap-2 sticky bottom-0 bg-[#F3F6EF] py-3 z-10">
            <button data-testid="btn-save-plan-private" onClick={save} disabled={plan.status !== "draft"} className="flex items-center justify-center gap-2 bg-[#163A2B] hover:bg-[#0F2A1F] text-white font-semibold py-2.5 rounded-xl transition-colors disabled:opacity-60">
              <Save className="w-4 h-4" /> {plan.status === "draft" ? "Save privately" : "Saved"}
            </button>
            <button data-testid="btn-share-plan-friends" onClick={() => setShare(true)} className="flex items-center justify-center gap-2 border border-stone-300 bg-white hover:bg-stone-50 font-semibold py-2.5 rounded-xl transition-colors">
              <Share2 className="w-4 h-4" /> Share with friends
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {plan.status !== "completed" ? (
              <button data-testid="btn-open-checkin" onClick={() => setCheckin(true)} className="col-span-2 flex items-center justify-center gap-2 bg-[#2F7D55] hover:bg-[#23603F] text-white font-semibold py-2.5 rounded-xl transition-colors">
                <PartyPopper className="w-4 h-4" /> We went! How was it?
              </button>
            ) : <p className="col-span-2 text-center text-sm text-[#163A2B] font-semibold" data-testid="plan-completed-note">Completed and in your journal.</p>}
            <button data-testid="btn-edit-inputs" onClick={onEdit} className="flex items-center justify-center gap-1.5 text-sm text-stone-600 hover:text-stone-900 py-1.5"><SlidersHorizontal className="w-4 h-4" /> Change inputs</button>
            <button data-testid="btn-new-plan" onClick={() => nav("/")} className="flex items-center justify-center gap-1.5 text-sm text-stone-600 hover:text-stone-900 py-1.5"><RotateCcw className="w-4 h-4" /> New plan</button>
          </div>
        </>
      )}
      <SponsoredAds plan={plan} />
      <p className="text-[11px] text-stone-400 leading-relaxed">Places from OpenStreetMap contributors. Hours, prices and travel times are estimates. Check before you go, and skip anything that doesn't feel right.</p>

      {owner && <PickPlaceDialog open={!!pick} onOpenChange={(o) => !o && setPick(null)} planId={plan.id} replacing={pick?.replacing} onPick={onPick} />}
      <ReasonDialog open={!!dislike} onOpenChange={(o) => !o && setDislike(null)} title="Not for you? Tell us why" description="We'll avoid similar suggestions next time." options={DISLIKE_REASONS} testPrefix="dislike"
        onSubmit={async (reason, note) => { await api.post("/feedback", { plan_id: plan.id, sid: dislike.sid, vote: "down", reason: note ? `${reason}: ${note}` : reason }); setPlan({ ...plan, stops: plan.stops.map((s) => (s.sid === dislike.sid ? { ...s, feedback: "down" } : s)) }); setDislike(null); toast.success("Got it. Want to replace it?", { action: { label: "Replace", onClick: () => setPick({ replacing: dislike }) } }); }} />
      <ReasonDialog open={!!report} onOpenChange={(o) => !o && setReport(null)} title={`Report a problem${report ? ` with ${report.name}` : ""}`} description="Thanks for keeping places accurate. We'll flag it and stop suggesting it to you." options={REPORT_ISSUES} testPrefix="report" cta="Send report"
        onSubmit={async (issue, note) => { try { const { data } = await api.post("/reports", { plan_id: plan.id, sid: report.sid, issue, note }); setPlan(data); toast.success("Reported. Thank you."); } catch (e) { toast.error(errMsg(e)); } setReport(null); }} />
      {owner && <ShareDialog open={share} onOpenChange={setShare} plan={plan} setPlan={setPlan} />}
      {owner && checkin && <CheckinDialog open={checkin} onOpenChange={setCheckin} plan={plan} />}
    </div>
  );
};
